import { AxiosResponse } from 'axios';
import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { patchMerchantOverview, putRuleOverrides, postRuleOverrides, patchTenantGroupOverrides } from 'Services';
import {
    RQ_GLOBAL_RULES,
    RQ_MERCHANT_RULES,
    RQ_RULE_REVISION_MERCHANT_OVERRIDES,
    RQ_TENANT_OVERRIDES,
    RQ_TENANT_GROUP_OVERRIDES,
    RQ_TENANT_GROUP_OVERRIDES_INHERITED,
    RQ_RULE_REVISION_TENANT_GROUP_OVERRIDES,
    RQ_TENANT_GROUPS,
    RQ_MERCHANT_OVERRIDES
} from 'Utils';
import useTenantErrorHandler from 'Hooks/query-hooks/tenant/useTenantErrorHandler';
import useTenantGroupOverrideErrorHandler from 'Hooks/query-hooks/tenant/useTenantGroupOverrideErrorHandler';
import useSingleMerchantErrorHandler from './useSingleMerchantErrorHandler';

type Args = {
    repoId: number;
    merchantId: string;
    revision: number;
    mutationOptions?: UseMutationOptions<AxiosResponse<any>, unknown, RecursivePartial<GlobalRulesData>, unknown>;
    actionType?: OverrideAction;
    overrideLevel?: OverrideLevel;
    tenantGroupId?: number;
    tenantGroupName?: string;
};

const editOverview = (
    templateRepositoryId: number,
    merchantId: string,
    revision: number,
    tenantGroupId: number | undefined,
    overrideLevel?: OverrideLevel,
    actionType?: OverrideAction
) => (data: RecursivePartial<GlobalRulesData>) => {
    if (overrideLevel === 'merchant') {
        return patchMerchantOverview({ templateRepositoryId, merchantId, revision, ...data });
    }
    if (overrideLevel === 'tenantGroup') {
        return patchTenantGroupOverrides({ templateRepositoryId, tenantGroupId, revision, ...data });
    }
    // tenant override
    const payload: RuleOverride = { parameters: {}, ruleId: '', actions: [] };
    if (data.rules && data.rules.length > 0) {
        // we're going to assume only one rule is changed per save
        payload.parameters = data.rules[0].parameters as { [key: string]: any };
        payload.ruleId = data.rules[0].id || '';
        if (data.comments) {
            payload.comments = data.comments;
        }
        if (data.rules[0].lists && data.rules[0].lists.length > 0) {
            payload.lists = data.rules[0].lists;
        }
        payload.actions = data.rules[0].actions ?? [];
    }
    if (actionType === 'add') {
        return postRuleOverrides(payload);
    }
    return putRuleOverrides(payload); // tenant edit
};

export default function useUpdateMerchantOverview({
    repoId,
    merchantId,
    revision,
    mutationOptions: { onSuccess, onError, ...rest } = {},
    actionType,
    overrideLevel,
    tenantGroupId,
    tenantGroupName
}: Args) {
    const queryClient = useQueryClient();
    const merchantOverrideErrorHandler = useSingleMerchantErrorHandler({
        mode: 'overview',
        queryInvalidateFunction: () => queryClient.invalidateQueries([RQ_MERCHANT_RULES, repoId, merchantId]),
        mutationOptions: {
            onError
        }
    });
    const tenantOverrideErrorHandler = useTenantErrorHandler({
        queryInvalidateFunction: () => queryClient.invalidateQueries([RQ_TENANT_OVERRIDES]),
        mutationOptions: {
            onError
        },
        mode: 'updateTenantOverride'
    });

    const tenantGroupOverrideErrorHandler = useTenantGroupOverrideErrorHandler({
        queryInvalidateFunction: () => {
            queryClient.invalidateQueries([RQ_TENANT_GROUP_OVERRIDES]);
            queryClient.invalidateQueries([RQ_TENANT_GROUP_OVERRIDES_INHERITED]);
            queryClient.invalidateQueries([RQ_TENANT_GROUPS]);
        },
        mutationOptions: {
            onError
        },
        mode: 'updateTenantGroupOverride',
        tenantGroupId,
        tenantGroupName
    });

    const ret = useMutation(editOverview(repoId, merchantId, revision, tenantGroupId, overrideLevel, actionType), {
        onSuccess: (data, variables, context) => {
            switch (overrideLevel) {
                case 'merchant':
                    queryClient.invalidateQueries([RQ_MERCHANT_OVERRIDES]);
                    queryClient.invalidateQueries([RQ_MERCHANT_RULES, repoId, merchantId]);
                    queryClient.invalidateQueries([RQ_RULE_REVISION_MERCHANT_OVERRIDES]);
                    break;
                case 'tenant':
                    queryClient.invalidateQueries([RQ_TENANT_OVERRIDES]);
                    queryClient.invalidateQueries([RQ_GLOBAL_RULES]);
                    break;
                default:
                    queryClient.invalidateQueries([RQ_TENANT_GROUP_OVERRIDES]);
                    queryClient.invalidateQueries([RQ_TENANT_GROUP_OVERRIDES_INHERITED]);
                    queryClient.invalidateQueries([RQ_RULE_REVISION_TENANT_GROUP_OVERRIDES]);
                    break;
            }

            if (onSuccess) {
                onSuccess(data, variables, context);
            }
        },

        onError: async (error: any) => {
            switch (overrideLevel) {
                case 'merchant':
                    merchantOverrideErrorHandler(error, ret.reset);
                    break;
                case 'tenant':
                    tenantOverrideErrorHandler(error, ret.reset);
                    break;
                default:
                    tenantGroupOverrideErrorHandler(error, ret.reset);
                    break;
            }
        },
        ...rest
    });

    return ret;
}
